import { Suspense, lazy } from "react";
import { Routes, Route } from "react-router-dom";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { AppSidebar } from "@/components/app-sidebar";
import { RoleGuard } from "@/components/common/RoleGuard";
import { TENANT_SIDEBAR } from "@/routes";
import { useLocation } from "react-router-dom";
import { STRINGS } from "@/constants/strings";

const DashboardPage = lazy(() => import("@/pages/tenant/DashboardPage"));
const PatientsPage = lazy(() => import("@/pages/tenant/PatientsPage"));
const PatientDetailPage = lazy(
  () => import("@/pages/tenant/PatientDetailPage"),
);
const SessionDetailPage = lazy(
  () => import("@/pages/tenant/SessionDetailPage"),
);
const BundlesPage = lazy(() => import("@/pages/tenant/BundlesPage"));
const BundleDetailTenantPage = lazy(
  () => import("@/pages/tenant/BundleDetailTenantPage"),
);
const UsersPage = lazy(() => import("@/pages/tenant/UsersPage"));
const BrandingPage = lazy(() => import("@/pages/tenant/BrandingPage"));
const AuditLogPage = lazy(() => import("@/pages/tenant/AuditLogPage"));

function PageTitle() {
  const location = useLocation();
  const current = TENANT_SIDEBAR.find((item) =>
    item.path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(item.path),
  );
  return current?.title ?? STRINGS.app.name;
}

export function TenantShell() {
  return (
    <SidebarProvider>
      <AppSidebar shell="tenant" items={TENANT_SIDEBAR} />
      <SidebarInset>
        <header className="flex h-10 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 my-auto h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <PageTitle />
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Suspense
            fallback={
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                {STRINGS.tenant.loading}
              </div>
            }
          >
            <Routes>
              <Route
                index
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "medico_hcp", "infermiere_hcp", "admin_clinico", "medico", "infermiere"]}>
                    <DashboardPage />
                  </RoleGuard>
                }
              />
              <Route
                path="patients"
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "tecnico", "medico_hcp", "infermiere_hcp", "admin_clinico", "medico", "infermiere"]}>
                    <PatientsPage />
                  </RoleGuard>
                }
              />
              <Route
                path="patients/:id"
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "tecnico", "medico_hcp", "infermiere_hcp", "admin_clinico", "medico", "infermiere"]}>
                    <PatientDetailPage />
                  </RoleGuard>
                }
              />
              <Route
                path="patients/:id/sessions/:sessionId"
                element={
                  <RoleGuard allowedRoles={["medico_hcp", "infermiere_hcp", "medico", "infermiere"]}>
                    <SessionDetailPage />
                  </RoleGuard>
                }
              />
              <Route
                path="bundles"
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "tecnico"]}>
                    <BundlesPage />
                  </RoleGuard>
                }
              />
              <Route
                path="bundles/:bundleId"
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "tecnico"]}>
                    <BundleDetailTenantPage />
                  </RoleGuard>
                }
              />
              <Route
                path="users"
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "admin_clinico"]}>
                    <UsersPage />
                  </RoleGuard>
                }
              />
              <Route
                path="branding"
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "admin_clinico"]}>
                    <BrandingPage />
                  </RoleGuard>
                }
              />
              <Route
                path="audit"
                element={
                  <RoleGuard allowedRoles={["admin_hcp", "admin_clinico"]}>
                    <AuditLogPage />
                  </RoleGuard>
                }
              />
            </Routes>
          </Suspense>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
